import { useEffect, useState } from "react";
import { ShoppingCart, FilePdf, EnvelopeSimple, Plus, Minus, ArrowClockwise, CheckCircle, Clock } from "@phosphor-icons/react";
import { orderSuggestions, createOrder, listOrders, orderPdfUrl, sendOrderEmail } from "@/lib/api";
import { toast } from "sonner";

export default function Orders() {
  const [groups, setGroups] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [emailDialog, setEmailDialog] = useState(null); // order

  const load = async () => {
    setLoading(true);
    const [g, h] = await Promise.all([orderSuggestions(), listOrders()]);
    setGroups(g.map((gr) => ({ ...gr, expanded: true, notes: "" })));
    setHistory(h);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const updateQty = (gi, li, qty) => {
    setGroups((prev) => {
      const next = [...prev];
      next[gi] = { ...next[gi], lines: next[gi].lines.map((l, i) => i === li ? { ...l, qty: Math.max(0, qty) } : l) };
      return next;
    });
  };

  const validate = async (gi) => {
    const g = groups[gi];
    const lines = g.lines.filter((l) => l.qty > 0).map(({ stock, min_threshold, avg_daily, ...rest }) => rest);
    if (lines.length === 0) {
      toast.error("Aucune quantité à commander");
      return;
    }
    try {
      const order = await createOrder({ supplier_id: g.supplier_id || "none", lines, notes: g.notes || null });
      toast.success("Commande créée", { description: `${lines.length} produit(s) · ${order.total.toFixed(2)} €` });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  return (
    <div className="fade-up space-y-6">
      <header className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.15em] font-bold text-[color:var(--text-muted)]">Réapprovisionnement</div>
          <h1 className="text-4xl font-heading font-medium mt-1">Commandes</h1>
          <p className="text-[color:var(--text-muted)] mt-1 max-w-2xl">Les produits sous le seuil sont automatiquement suggérés. Quantités calculées selon la consommation et le délai fournisseur.</p>
        </div>
        <button onClick={load} className="h-12 px-4 rounded-2xl border border-[color:var(--border-soft)] bg-white flex items-center gap-2 press-scale" data-testid="btn-refresh">
          <ArrowClockwise size={20} /> Actualiser
        </button>
      </header>

      {loading && <div className="text-center py-8 text-[color:var(--text-muted)]">Calcul en cours…</div>}

      {!loading && groups.length === 0 && (
        <div className="bg-white rounded-3xl border border-[color:var(--border-soft)] p-12 text-center">
          <CheckCircle size={48} weight="duotone" className="mx-auto text-[color:var(--brand)]" />
          <div className="font-heading text-xl font-medium mt-4">Tout est en ordre</div>
          <div className="text-[color:var(--text-muted)] mt-1">Aucun produit sous le seuil minimum.</div>
        </div>
      )}

      <div className="space-y-4">
        {groups.map((g, gi) => (
          <div key={g.supplier_id || "none"} className="bg-white rounded-3xl border border-[color:var(--border-soft)] overflow-hidden" data-testid={`group-${g.supplier_id || "none"}`}>
            <div className="p-5 border-b border-[color:var(--border-soft)] flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="font-heading text-lg font-semibold">{g.supplier_name}</div>
                {g.supplier_email && <div className="text-xs text-[color:var(--text-muted)]">{g.supplier_email}</div>}
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm text-[color:var(--text-muted)]">{g.lines.length} produit(s) · <b className="text-[color:var(--text-primary)]">{g.lines.reduce((a, l) => a + l.qty * l.unit_price, 0).toFixed(2)} €</b></span>
                <button onClick={() => validate(gi)} className="h-12 px-5 bg-[color:var(--brand)] text-white rounded-2xl flex items-center gap-2 press-scale hover:bg-[color:var(--brand-hover)]" data-testid={`btn-validate-${g.supplier_id || "none"}`}>
                  <ShoppingCart size={20} weight="bold" /> Générer la commande
                </button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[color:var(--bg)]">
                  <tr className="text-left">
                    <th className="py-2.5 px-4 text-xs uppercase tracking-wider font-bold text-[color:var(--text-muted)]">Produit</th>
                    <th className="py-2.5 px-4 text-xs uppercase tracking-wider font-bold text-[color:var(--text-muted)]">Stock</th>
                    <th className="py-2.5 px-4 text-xs uppercase tracking-wider font-bold text-[color:var(--text-muted)]">Conso/j</th>
                    <th className="py-2.5 px-4 text-xs uppercase tracking-wider font-bold text-[color:var(--text-muted)]">Suggéré</th>
                    <th className="py-2.5 px-4 text-xs uppercase tracking-wider font-bold text-[color:var(--text-muted)]">À commander</th>
                    <th className="py-2.5 px-4 text-xs uppercase tracking-wider font-bold text-[color:var(--text-muted)]">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {g.lines.map((l, li) => (
                    <tr key={l.product_id} className="border-t border-[color:var(--border-soft)]">
                      <td className="py-3 px-4 font-medium">{l.product_name}</td>
                      <td className="py-3 px-4 tabular-nums text-[color:var(--text-muted)]">{l.stock} {l.unit}</td>
                      <td className="py-3 px-4 tabular-nums text-[color:var(--text-muted)]">{l.avg_daily}</td>
                      <td className="py-3 px-4 tabular-nums text-[color:var(--text-muted)]">{l.suggested_qty}</td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-1">
                          <button onClick={() => updateQty(gi, li, l.qty - 1)} className="w-10 h-10 rounded-xl bg-[color:var(--brand-muted)] text-[color:var(--brand)] flex items-center justify-center press-scale" data-testid={`line-minus-${l.product_id}`}>
                            <Minus size={18} weight="bold" />
                          </button>
                          <input
                            data-testid={`line-qty-${l.product_id}`}
                            type="number"
                            value={l.qty}
                            onChange={(e) => updateQty(gi, li, Number(e.target.value))}
                            className="w-20 h-10 text-center rounded-xl border border-[color:var(--border-soft)] focus:outline-none focus:ring-2 focus:ring-[color:var(--brand)]"
                          />
                          <button onClick={() => updateQty(gi, li, l.qty + 1)} className="w-10 h-10 rounded-xl bg-[color:var(--brand)] text-white flex items-center justify-center press-scale" data-testid={`line-plus-${l.product_id}`}>
                            <Plus size={18} weight="bold" />
                          </button>
                        </div>
                      </td>
                      <td className="py-3 px-4 font-semibold tabular-nums">{(l.qty * l.unit_price).toFixed(2)} €</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>

      <section className="bg-white rounded-3xl border border-[color:var(--border-soft)] p-6">
        <div className="flex items-center gap-2 mb-4">
          <Clock size={22} className="text-[color:var(--brand)]" />
          <h2 className="font-heading text-lg font-medium">Historique des commandes</h2>
        </div>
        {history.length === 0 ? (
          <div className="text-[color:var(--text-muted)] text-center py-6">Aucune commande encore.</div>
        ) : (
          <ul className="space-y-2" data-testid="order-history">
            {history.map((o) => (
              <li key={o.id} className="flex items-center gap-4 p-3 rounded-2xl border border-[color:var(--border-soft)] hover:bg-[color:var(--bg)]">
                <div className="flex-1 min-w-0">
                  <div className="font-medium">{o.supplier_name}</div>
                  <div className="text-xs text-[color:var(--text-muted)] flex gap-2 flex-wrap">
                    <span>{new Date(o.created_at).toLocaleString("fr-FR")}</span>
                    <span>·</span>
                    <span>{o.lines.length} ligne(s)</span>
                    <span>·</span>
                    <span>{o.total.toFixed(2)} €</span>
                    <span>·</span>
                    <span className={`font-semibold ${o.status === "sent" ? "text-[color:var(--brand)]" : "text-[color:var(--text-muted)]"}`}>{o.status === "sent" ? "Envoyée" : "Brouillon"}</span>
                  </div>
                </div>
                <a href={orderPdfUrl(o.id)} target="_blank" rel="noreferrer" className="h-11 px-4 rounded-xl border border-[color:var(--border-soft)] flex items-center gap-2 press-scale text-sm font-medium" data-testid={`order-pdf-${o.id}`}>
                  <FilePdf size={18} /> PDF
                </a>
                <button onClick={() => setEmailDialog(o)} className="h-11 px-4 rounded-xl bg-[color:var(--brand)] text-white flex items-center gap-2 press-scale hover:bg-[color:var(--brand-hover)] text-sm font-medium" data-testid={`order-email-${o.id}`}>
                  <EnvelopeSimple size={18} /> Email
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {emailDialog && <EmailDialog order={emailDialog} onClose={() => setEmailDialog(null)} onSent={load} />}
    </div>
  );
}

function EmailDialog({ order, onClose, onSent }) {
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    (async () => {
      const suppliers = await import("@/lib/api").then((m) => m.listSuppliers());
      const s = suppliers.find((x) => x.id === order.supplier_id);
      setEmail(s?.email || "");
    })();
  }, [order]);

  const send = async () => {
    if (!email) { toast.error("Email destinataire requis"); return; }
    setSending(true);
    try {
      await sendOrderEmail(order.id, { recipient_email: email, message: msg || null });
      toast.success(`Email envoyé à ${email}`);
      onSent();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="email-dialog">
      <div className="bg-white rounded-3xl w-full max-w-md p-6">
        <h3 className="font-heading text-xl font-semibold mb-1">Envoyer la commande</h3>
        <p className="text-sm text-[color:var(--text-muted)] mb-4">Un email avec le PDF en pièce jointe sera envoyé au fournisseur.</p>
        <label className="block mb-3">
          <span className="text-xs uppercase tracking-[0.1em] font-bold text-[color:var(--text-muted)]">Destinataire</span>
          <input data-testid="email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 w-full h-14 px-4 rounded-2xl border border-[color:var(--border-soft)] focus:outline-none focus:ring-2 focus:ring-[color:var(--brand)]" />
        </label>
        <label className="block mb-4">
          <span className="text-xs uppercase tracking-[0.1em] font-bold text-[color:var(--text-muted)]">Message (optionnel)</span>
          <textarea data-testid="email-msg" value={msg} onChange={(e) => setMsg(e.target.value)} rows={3} className="mt-1 w-full p-3 rounded-2xl border border-[color:var(--border-soft)] focus:outline-none focus:ring-2 focus:ring-[color:var(--brand)]" />
        </label>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="h-12 px-5 rounded-2xl border border-[color:var(--border-soft)]" data-testid="email-cancel">Annuler</button>
          <button onClick={send} disabled={sending} className="h-12 px-6 bg-[color:var(--brand)] text-white rounded-2xl press-scale hover:bg-[color:var(--brand-hover)] disabled:opacity-60" data-testid="email-send">
            {sending ? "Envoi…" : "Envoyer"}
          </button>
        </div>
      </div>
    </div>
  );
}
